import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../utils/api';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  MoreVert as MoreVertIcon,
  CloudUpload as CloudUploadIcon,
  FileDownload as FileDownloadIcon,
  CloudDownload as CloudDownloadIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState('add');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category_id: null,
    organization: '',
    phone: '',
    email: '',
    region: '',
    position: '',
    status_id: null,
    memo: '',
  });
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [lastInteractions, setLastInteractions] = useState({});
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sortModel, setSortModel] = useState(() => {
    try {
      const savedSortModel = localStorage.getItem('customerSortModel');
      return savedSortModel ? JSON.parse(savedSortModel) : [];
    } catch (error) {
      console.error('정렬 상태 복원 오류:', error);
      return [];
    }
  });
  const [paginationModel, setPaginationModel] = useState({
    pageSize: parseInt(localStorage.getItem('customerPageSize') || '10'),
    page: parseInt(localStorage.getItem('customerPage') || '0'),
  });
  const [openExcelDialog, setOpenExcelDialog] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomers();
    fetchCategories();
    fetchStatuses();
  }, []);

  useEffect(() => {
    if (!customers.length) {
      setFilteredCustomers([]);
      return;
    }
    
    let filtered = [...customers];
    
    if (categoryFilter) {
      filtered = filtered.filter(customer => 
        customer.category_id === parseInt(categoryFilter)
      );
    }
    
    if (statusFilter) {
      filtered = filtered.filter(customer => 
        customer.status_id === parseInt(statusFilter)
      );
    }
    
    setFilteredCustomers(filtered);
  }, [customers, categoryFilter, statusFilter]);

  const handleCategoryFilterChange = (e) => {
    setCategoryFilter(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleResetFilters = () => {
    setCategoryFilter('');
    setStatusFilter('');
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/customers');
      setCustomers(res.data);
      setFilteredCustomers(res.data);
      fetchLastInteractions(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || '고객 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      const sortedCategories = res.data.sort((a, b) => a.sort_order - b.sort_order);
      setCategories(sortedCategories);
    } catch (error) {
      console.error('카테고리 목록을 불러오는데 실패했습니다.', error);
    }
  };

  const fetchStatuses = async () => {
    try {
      const res = await api.get('/statuses');
      const sortedStatuses = res.data.sort((a, b) => a.order - b.order);
      setStatuses(sortedStatuses);
    } catch (error) {
      console.error('상태 목록을 불러오는데 실패했습니다.', error);
    }
  };

  const fetchLastInteractions = async (customerList) => {
    try {
      const interactionsMap = {};
      
      for (const customer of customerList) {
        try {
          const res = await api.get(`/interactions/customer/${customer.id}`);
          if (res.data.length > 0) {
            const sortedInteractions = res.data.sort((a, b) => 
              new Date(b.date) - new Date(a.date)
            );
            interactionsMap[customer.id] = sortedInteractions[0];
          }
        } catch (error) {
          console.error(`Failed to fetch interactions for customer ${customer.id}`, error);
        }
      }
      
      setLastInteractions(interactionsMap);
    } catch (error) {
      console.error('Error fetching interactions', error);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const url = searchTerm
        ? `/customers/search?q=${encodeURIComponent(searchTerm)}`
        : '/customers';
        
      const res = await api.get(url);
      setCustomers(res.data);
      fetchLastInteractions(res.data);
      setError(null);
      
      // 검색 후 필터도 다시 적용
      let filtered = [...res.data];
      
      // 카테고리 필터 적용
      if (categoryFilter) {
        filtered = filtered.filter(customer => 
          customer.category_id === parseInt(categoryFilter)
        );
      }
      
      // 상태 필터 적용
      if (statusFilter) {
        filtered = filtered.filter(customer => 
          customer.status_id === parseInt(statusFilter)
        );
      }
      
      setFilteredCustomers(filtered);
    } catch (err) {
      setError(err.response?.data?.message || '검색 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value === '') {
      fetchCustomers();
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const resetFormData = () => {
    setFormData({
      name: '',
      category_id: null,
      organization: '',
      phone: '',
      email: '',
      region: '',
      position: '',
      status_id: null,
      memo: '',
    });
  };

  const handleOpenDialog = (action, customer = null) => {
    setDialogAction(action);
    
    if (action === 'edit' && customer) {
      setSelectedCustomer(customer);
      setFormData({
        name: customer.name || '',
        category_id: customer.category_id || null,
        organization: customer.organization || '',
        phone: customer.phone || '',
        email: customer.email || '',
        region: customer.region || '',
        position: customer.position || '',
        status_id: customer.status_id || null,
        memo: customer.memo || '',
      });
    } else {
      resetFormData();
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetFormData();
  };

  const handleSubmit = async () => {
    try {
      if (dialogAction === 'add') {
        await api.post('/customers', formData);
        handleCloseDialog();
        fetchCustomers();
      } else {
        await api.put(`/customers/${selectedCustomer.id}`, formData);
        handleCloseDialog();
        
        if (searchTerm) {
          handleSearch();
        } else {
          fetchCustomers();
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || '저장하는 중 오류가 발생했습니다.');
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDeleteClick = (customer) => {
    setCustomerToDelete(customer);
    setDeleteConfirm(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/customers/${customerToDelete.id}`);
      setDeleteConfirm(false);
      
      // 검색어가 있는 경우 검색 상태 유지
      if (searchTerm) {
        handleSearch(); // 현재 검색어로 검색 다시 실행
      } else {
        fetchCustomers(); // 검색어가 없으면 전체 목록 갱신
      }
      
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || '삭제하는 중 오류가 발생했습니다.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBackgroundColor = (statusName) => {
    if (!statusName) return 'transparent';
    const status = statuses.find(s => s.name === statusName);
    return status ? status.color : 'transparent';
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    localStorage.setItem('customerSortModel', JSON.stringify(newSortModel));
  };

  const getLastContactValue = (params) => {
    const lastInteraction = lastInteractions[params.row.id];
    return lastInteraction ? formatDate(lastInteraction.date) : '-';
  };

  const columns = [
    { field: 'name', headerName: '이름', flex: 2, minWidth: 240, resizable: true },
    { 
      field: 'category_name', 
      headerName: '구분', 
      width: 140,
      valueGetter: (params) => params.row.category_name || '-',
      resizable: true
    },
    { field: 'organization', headerName: '소속', width: 170, resizable: true },
    { field: 'phone', headerName: '전화번호', width: 140, resizable: true },
    { 
      field: 'email', 
      headerName: '이메일', 
      width: 170,
      resizable: true,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          sx={{ 
            overflow: 'visible',
            whiteSpace: 'normal',
            lineHeight: '1.2',
            wordBreak: 'break-all'
          }}
        >
          {params.value || ''}
        </Typography>
      )
    },
    { field: 'region', headerName: '지역', width: 90, resizable: true },
    { 
      field: 'status_name', 
      headerName: '상태', 
      width: 80,
      align: 'center',
      headerAlign: 'center',
      resizable: true,
      renderCell: (params) => (
        <Box
          sx={{
            width: '24px',
            height: '24px',
            backgroundColor: params.row.status_color || 'transparent',
            borderRadius: '4px',
            border: params.row.status_color ? 'none' : '1px dashed #ccc',
            margin: '0 auto'
          }}
          title={params.row.status_name || '상태 없음'}
        />
      )
    },
    {
      field: 'lastContact',
      headerName: '최근 연락',
      width: 170,
      valueGetter: getLastContactValue,
      resizable: true
    },
    {
      field: 'memo',
      headerName: '메모',
      width: 190,
      sortable: true,
      resizable: true,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          sx={{ 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
          }}
          title={params.row.memo || ''}
        >
          {params.row.memo || ''}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: '작업',
      width: 110,
      sortable: false,
      resizable: true,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => navigate(`/customers/${params.row.id}`)}
            title="상세 보기"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog('edit', params.row)}
            title="편집"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(params.row)}
            title="삭제"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      setUploadFile(acceptedFiles[0]);
      setUploadError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
  });

  const handleOpenExcelDialog = () => {
    setOpenExcelDialog(true);
    setUploadFile(null);
    setUploadResult(null);
    setUploadError(null);
  };

  const handleCloseExcelDialog = () => {
    setOpenExcelDialog(false);
    setUploadFile(null);
    setUploadResult(null);
    setUploadError(null);
  };

  const handleExcelUpload = async () => {
    if (!uploadFile) {
      setUploadError('업로드할 파일을 선택해주세요.');
      return;
    }

    try {
      setUploadLoading(true);
      setUploadError(null);

      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await api.post('/customers/import/excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResult(response.data);
      fetchCustomers();
    } catch (error) {
      console.error('엑셀 업로드 오류:', error);
      setUploadError(error.response?.data?.message || '파일 업로드 중 오류가 발생했습니다.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${process.env.REACT_APP_API_URL}/customers/export/template`, '_blank');
  };

  const handleDownloadExcel = () => {
    window.open(`${process.env.REACT_APP_API_URL}/customers/export/excel`, '_blank');
  };

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
    localStorage.setItem('customerPage', newPaginationModel.page.toString());
    localStorage.setItem('customerPageSize', newPaginationModel.pageSize.toString());
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h1">
          고객 목록
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<CloudDownloadIcon />}
            onClick={handleDownloadExcel}
            sx={{ mr: 1 }}
          >
            엑셀 내려받기
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={handleOpenExcelDialog}
            sx={{ mr: 1 }}
          >
            엑셀 가져오기
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
          >
            고객 추가
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="고객 검색(이름, 소속, 전화번호, 이메일, 지역, 직급)"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', mb: 3, gap: 2, alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>구분 필터</InputLabel>
          <Select
            value={categoryFilter}
            onChange={handleCategoryFilterChange}
            label="구분 필터"
          >
            <MenuItem value="">선택 안함</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>상태 필터</InputLabel>
          <Select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="상태 필터"
          >
            <MenuItem value="">선택 안함</MenuItem>
            {statuses.map((status) => (
              <MenuItem 
                key={status.id} 
                value={status.id}
                sx={{
                  backgroundColor: status.color || 'transparent',
                  '&:hover': {
                    backgroundColor: status.color ? `${status.color}!important` : 'inherit'
                  }
                }}
              >
                {status.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {(categoryFilter || statusFilter) && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleResetFilters}
          >
            필터 초기화
          </Button>
        )}
        
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          총 {filteredCustomers.length}명의 고객
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ height: 'calc(100vh - 350px)', width: '100%' }}>
        <DataGrid
          rows={filteredCustomers}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: paginationModel,
            },
          }}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          disableSelectionOnClick
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          loading={loading}
          columnResizeMode="growAndShrink"
          disableColumnMenu={false}
          columnVisibilityModel={{}}
          density="standard"
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  등록된 고객이 없습니다.
                </Typography>
              </Box>
            ),
          }}
        />
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogAction === 'add' ? '새 고객 추가' : '고객 정보 수정'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                name="name"
                label="이름"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>구분</InputLabel>
                <Select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                  label="구분"
                >
                  <MenuItem value="">선택 안함</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="organization"
                label="소속"
                fullWidth
                variant="outlined"
                value={formData.organization}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="phone"
                label="전화번호"
                fullWidth
                variant="outlined"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="email"
                label="이메일"
                type="email"
                fullWidth
                variant="outlined"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="region"
                label="지역"
                fullWidth
                variant="outlined"
                value={formData.region}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>상태</InputLabel>
                <Select
                  name="status_id"
                  value={formData.status_id}
                  onChange={handleInputChange}
                  label="상태"
                >
                  <MenuItem value="">선택 안함</MenuItem>
                  {statuses.map((status) => (
                    <MenuItem 
                      key={status.id} 
                      value={status.id}
                      sx={{
                        backgroundColor: status.color || 'transparent',
                        '&:hover': {
                          backgroundColor: status.color ? `${status.color}!important` : 'inherit'
                        }
                      }}
                    >
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="position"
                label="직급"
                fullWidth
                variant="outlined"
                value={formData.position}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="memo"
                label="메모"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={formData.memo}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogAction === 'add' ? '추가' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>고객 삭제 확인</DialogTitle>
        <DialogContent>
          <DialogContentText>
            '{customerToDelete?.name}' 고객을 정말로 삭제하시겠습니까?
            이 작업은 되돌릴 수 없습니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)}>취소</Button>
          <Button onClick={handleDelete} color="error">
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openExcelDialog} onClose={handleCloseExcelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>엑셀 파일로 고객 가져오기</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            엑셀 파일을 업로드하여 고객 정보를 한 번에 가져올 수 있습니다.
            <br />
            올바른 형식으로 데이터를 준비해주세요.
          </DialogContentText>
          
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleDownloadTemplate}
            sx={{ mb: 2 }}
            fullWidth
          >
            엑셀 템플릿 다운로드
          </Button>
          
          <Box 
            {...getRootProps()}
            sx={{
              border: '2px dashed #cccccc',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: isDragActive ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
              cursor: 'pointer',
              mb: 2
            }}
          >
            <input {...getInputProps()} />
            {uploadFile ? (
              <Typography>
                선택된 파일: {uploadFile.name} ({Math.round(uploadFile.size / 1024)} KB)
              </Typography>
            ) : (
              <Typography>
                {isDragActive ? 
                  '파일을 여기에 놓으세요...' : 
                  '엑셀 파일을 여기에 끌어다 놓거나, 클릭하여 선택하세요.'
                }
              </Typography>
            )}
          </Box>
          
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}
          
          {uploadResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {uploadResult.successCount}명의 고객 정보가 성공적으로 가져와졌습니다.
              {uploadResult.duplicateCount > 0 && ` ${uploadResult.duplicateCount}명은 이미 등록된 중복 고객입니다.`}
              {uploadResult.errorCount > 0 && ` ${uploadResult.errorCount}개의 행에서 오류가 발생했습니다.`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExcelDialog}>취소</Button>
          <Button 
            onClick={handleExcelUpload} 
            variant="contained" 
            disabled={!uploadFile || uploadLoading}
            startIcon={uploadLoading && <CircularProgress size={20} color="inherit" />}
          >
            {uploadLoading ? '업로드 중...' : '업로드'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers; 